import { useNuxtApp } from '#app';
import { BaseService } from '../base/base.service';
import { AiChatRepository } from './ai-chat.repository';
import type { AiChatSSEEvent, IModelPOSTAiChatSendRequest, IModelPOSTAiChatRatingRequest } from './dto/ai-chat.dto';

export class AiChatService extends BaseService {
  private aiChatRepository = new AiChatRepository(
    this.api,
    this.configService
  );

  async submitRating(params: IModelPOSTAiChatRatingRequest): Promise<void> {
    const { $functions } = useNuxtApp();
    try {
      const response = await this.aiChatRepository.submitRating(params);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err: any) {
      $functions.notify.error({ text: err.message });
      console.error(err, AiChatService.name);
    }
  }

  async sendMessage(params: IModelPOSTAiChatSendRequest): Promise<void> {
    const { $functions } = useNuxtApp();
    try {
      const response = await this.aiChatRepository.sendMessage(params);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      console.log('sendMessage', params);
    } catch (err: any) {
      $functions.notify.error({ text: err.message });
      console.log(err, AiChatService.name);
    }
  }

  async *subscribeStream(
    sessionId: string,
    abortController?: AbortController
  ): AsyncGenerator<AiChatSSEEvent, void, unknown> {
    const { $functions } = useNuxtApp();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let isAborted = false;

    try {
      const response = await this.aiChatRepository.subscribeStream(
        sessionId,
        abortController?.signal
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      reader = response.body?.getReader() || null;
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      if (abortController?.signal) {
        abortController.signal.addEventListener('abort', () => {
          console.log('[SSE] Abort signal received - canceling reader');
          isAborted = true;
          if (reader) {
            reader
              .cancel('Stream aborted by user')
              .then(() => console.log('[SSE] Reader canceled successfully'))
              .catch((err) => console.error('[SSE] Reader cancel error:', err));
          }
        });
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      while (true) {
        if (isAborted || abortController?.signal.aborted) {
          console.log('[SSE] Stream aborted, breaking loop');
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
            continue;
          }

          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();

            if (data === '[DONE]') {
              return;
            }

            try {
              const parsed = JSON.parse(data);
              yield { event: currentEvent, data: parsed } as AiChatSSEEvent;
            } catch (e) {
              console.warn('[SSE] Failed to parse data:', data);
            }

            currentEvent = '';
          }
        }
      }
    } catch (err: any) {
      console.log('[SSE subscribeStream] Stream error:', err);
      if (err.name === 'AbortError') {
        console.log('[SSE subscribeStream] Stream aborted by user');
      } else {
        $functions.notify.error({ text: err.message });
        console.error(err, AiChatService.name);
      }
    } finally {
      if (reader) {
        try {
          await reader.cancel();
          console.log('[SSE subscribeStream] Reader cleanup done');
        } catch (e) {
          console.log('[SSE subscribeStream] Reader cleanup error (ignored):', e);
        }
      }
    }
  }
}
